import PropTypes from 'prop-types'
import {useCallback, useEffect, useRef, useState} from 'react'

import {photoAlt} from './StoryPhotoPrints'
import StoryPhotoViewer from './StoryPhotoViewer'
import {useFullStoryPhotos} from './hooks/useStoryPhotoImages'
import {PhotoIcon} from './icons'

import './storyPhotoGallery.scss'

// Start loading a story's photos a little before they scroll into view.
const LOAD_AHEAD_MARGIN = '600px 0px'

const ARROW_HINT = 'Use the left and right arrow keys to see the other photos in this story.'

/**
 * A story's photos in Preview (TK-3108, AC 5.2, 5.3, 5.5, 6.3): one photo
 * centered at its own shape, two or three side by side cropped to 4:3, each
 * in a white frame. The full stored copies load as the story nears the
 * screen. A photo opens larger in the viewer, and closing it returns focus to
 * the last photo viewed.
 */
const StoryPhotoGallery = ({photos}) => {
  const rootRef = useRef(null)
  const shotRefs = useRef([])
  const [nearScreen, setNearScreen] = useState(typeof window === 'undefined' || !window.IntersectionObserver)
  const [viewing, setViewing] = useState(null)

  useEffect(() => {
    if (nearScreen) return

    const observer = new window.IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setNearScreen(true)
        observer.disconnect()
      }
    }, {root: rootRef.current.closest('.life-story-story'), rootMargin: LOAD_AHEAD_MARGIN})

    observer.observe(rootRef.current)

    return () => observer.disconnect()
  }, [nearScreen])

  const sources = useFullStoryPhotos(photos.map(photo => photo['upload-id']), {enabled: nearScreen})

  const closeViewer = useCallback(lastIndex => {
    setViewing(null)

    const shot = shotRefs.current[lastIndex]

    if (shot) shot.focus()
  }, [])

  const count = photos.length

  return (
    <div className={`story-photo-gallery n${Math.min(count, 3)}`} ref={rootRef}>
      {
        photos.map((photo, index) => {
          const src = sources[photo['upload-id']]

          return (
            <div className='story-photo-gallery-frame' key={photo['upload-id']}>
              <button
                aria-label={`See photo ${index + 1} larger${count > 1 ? `. ${ARROW_HINT}` : ''}`}
                className='story-photo-gallery-shot'
                ref={element => { shotRefs.current[index] = element }}
                type='button'
                onClick={() => setViewing(index)}>
                {src && <img alt={photoAlt(photo)} src={src} />}
                {typeof src === 'undefined' && <span className='story-photo-gallery-placeholder' />}
                {src === null && <span className='story-photo-gallery-placeholder unavailable'><PhotoIcon size={32} /></span>}
              </button>
            </div>
          )
        })
      }
      {viewing !== null && <StoryPhotoViewer initialIndex={viewing} photos={photos} sources={sources} onClose={closeViewer} />}
    </div>
  )
}

StoryPhotoGallery.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.shape({'upload-id': PropTypes.string, filename: PropTypes.string})).isRequired
}

export default StoryPhotoGallery
